import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, Loader2, ArrowRight, Layers,
  FileCheck2, Sparkles,
} from "lucide-react";
import { API } from "@/api/client";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return "—"; }
};

/* PUBLIC scan-to-verify landing page — reached from the QR / short-link stamped
   onto every exported compliance report. Confirms authenticity, then converts
   the scanning accountant into a Misteck lead (organic B2B acquisition loop). */
export default function VerifyReport() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    fetch(`${API}/compliance/verify/${token}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setState({ loading: false, data: d }); })
      .catch(() => { if (alive) setState({ loading: false, data: { valid: false } }); });
    return () => { alive = false; };
  }, [token]);

  const d = state.data;
  const valid = d?.valid;

  return (
    <div className="w-full h-full overflow-y-auto bg-charcoal text-cream flex flex-col" data-testid="verify-report-page">
      {/* Brand bar */}
      <div className="px-6 pt-8 pb-5 border-b-2 border-gold/30">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">Misteck Life</p>
        <p className="font-serif text-2xl text-cream leading-tight">Ledger Asset Engine</p>
      </div>

      {/* Verification result */}
      <div className="px-6 py-7">
        {state.loading ? (
          <div className="flex flex-col items-center gap-3 py-10" data-testid="verify-loading">
            <Loader2 size={26} className="text-gold animate-spin" />
            <p className="font-mono text-[11px] text-cream/60 uppercase tracking-widest">Verifying document…</p>
          </div>
        ) : valid ? (
          <div data-testid="verify-valid">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-12 h-12 rounded-2xl bg-emerald-500/15 border-2 border-emerald-400 grid place-items-center shrink-0">
                <ShieldCheck size={24} className="text-emerald-400" />
              </span>
              <div>
                <p className="font-serif text-xl text-emerald-300 leading-tight">Authentic Report</p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-emerald-400/70">Integrity confirmed</p>
              </div>
            </div>
            <div className="rounded-2xl border-2 border-gold/40 bg-white/5 p-4 shadow-[3px_3px_0px_#000] space-y-2.5">
              {[
                ["Document Ref", d.document_ref],
                ["Generated", fmtDate(d.generated_at)],
                ["Jurisdiction", (d.region || "—").toUpperCase()],
                ["Account Class", (d.subscription_tier || "—").replace("tier_", "Tier ")],
                ["Format", (d.format || "pdf").toUpperCase()],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-cream/50">{k}</span>
                  <span className="font-mono text-[11px] text-gold font-bold">{v}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5 mt-3">
              <FileCheck2 size={12} className="text-gold/70 shrink-0" />
              <p className="font-mono text-[8px] text-cream/40 leading-tight">Generated securely via Misteck Life Ledger Asset Engine</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="verify-invalid">
            <ShieldAlert size={30} className="text-rust" />
            <p className="font-serif text-xl text-cream">Report Not Found</p>
            <p className="font-mono text-[10px] text-cream/50 max-w-[240px]">This verification code is invalid or has expired. Ask the sender for a freshly exported report.</p>
          </div>
        )}
      </div>

      {/* High-conversion B2B acquisition block */}
      <div className="mt-auto px-6 pb-8 pt-2">
        <div className="rounded-3xl border-2 border-gold bg-gradient-to-b from-gold/15 to-transparent p-5" data-testid="verify-cta-block">
          <div className="flex items-center gap-2 mb-2">
            <Layers size={16} className="text-gold" />
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Automated Multi-Asset Ledger</p>
          </div>
          <p className="font-serif text-2xl leading-tight text-cream mb-2">
            Stop chasing receipts. Let the ledger build itself.
          </p>
          <p className="font-mono text-[11px] leading-relaxed text-cream/60 mb-4">
            Misteck auto-compiles crypto, payouts, revenue &amp; expenses into audit-ready
            reports your accountant can trust — exported in one tap.
          </p>
          <button
            onClick={() => navigate("/")}
            data-testid="verify-signup-cta"
            className="w-full flex items-center justify-center gap-2 bg-gold text-charcoal border-2 border-black font-mono text-[11px] font-black uppercase tracking-widest py-3.5 rounded-full shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all"
          >
            <Sparkles size={14} /> Start Free with Misteck <ArrowRight size={14} />
          </button>
          <p className="text-center font-mono text-[8px] text-cream/30 mt-2.5 uppercase tracking-widest">No card required · Set up in minutes</p>
        </div>
      </div>
    </div>
  );
}
